import React, { useCallback, useEffect } from 'react';
import { RotateCcw, Play, AlertCircle, Table2, BarChart3, Sparkles, Loader2 } from 'lucide-react';
import { useReportBuilderState } from '../hooks/useReportBuilderState';
import {
  useDataSources,
  usePreviewReport,
  useExportReport,
  useAggregateReport,
  useAIGenerateConfig,
  useAIInsights,
} from '../hooks/useReportQueries';
import type { ExportFormat } from '../constants';
import AIReportPanel from './AIReportPanel';
import DataSourceSection from './DataSourceSection';
import FieldsSection from './FieldsSection';
import FiltersSection from './FiltersSection';
import SortSection from './SortSection';
import VisualizationSection from './VisualizationSection';
import TableView from './TableView';
import ChartView from './ChartView';
import InsightsView from './InsightsView';
import '../../../pages/Admin/CustomReportBuilder/CustomReportBuilder.css';

const CustomReportBuilder: React.FC = () => {
  const { data: dataSources, isLoading: dataSourcesLoading } = useDataSources();

  const selectedDataSource = useReportBuilderState(s => s.selectedDataSource);
  const selectedFields = useReportBuilderState(s => s.selectedFields);
  const filters = useReportBuilderState(s => s.filters);
  const sortBy = useReportBuilderState(s => s.sortBy);
  const sortOrder = useReportBuilderState(s => s.sortOrder);
  const currentPage = useReportBuilderState(s => s.currentPage);
  const pageSize = useReportBuilderState(s => s.pageSize);
  const activeView = useReportBuilderState(s => s.activeView);
  const chartType = useReportBuilderState(s => s.chartType);
  const chartGroupByField = useReportBuilderState(s => s.chartGroupByField);
  const chartAggregateField = useReportBuilderState(s => s.chartAggregateField);
  const chartAggregateFunction = useReportBuilderState(s => s.chartAggregateFunction);
  const reportName = useReportBuilderState(s => s.reportName);
  const setActiveView = useReportBuilderState(s => s.setActiveView);
  const setCurrentPage = useReportBuilderState(s => s.setCurrentPage);
  const resetBuilder = useReportBuilderState(s => s.resetBuilder);
  const setSelectedDataSource = useReportBuilderState(s => s.setSelectedDataSource);
  const setSelectedFields = useReportBuilderState(s => s.setSelectedFields);
  const setSortBy = useReportBuilderState(s => s.setSortBy);
  const setSortOrder = useReportBuilderState(s => s.setSortOrder);
  const setChartType = useReportBuilderState(s => s.setChartType);
  const setChartGroupByField = useReportBuilderState(s => s.setChartGroupByField);
  const setChartAggregateField = useReportBuilderState(s => s.setChartAggregateField);
  const setChartAggregateFunction = useReportBuilderState(s => s.setChartAggregateFunction);

  const previewMutation = usePreviewReport();
  const exportMutation = useExportReport();
  const aggregateMutation = useAggregateReport();
  const aiGenerateMutation = useAIGenerateConfig();
  const aiInsightsMutation = useAIInsights();

  const currentSource = dataSources?.[selectedDataSource];
  const hasReportConfig = !!selectedDataSource && selectedFields.length > 0;

  // Re-run preview when pagination changes (only if we have data)
  useEffect(() => {
    if (hasReportConfig && previewMutation.data) {
      previewMutation.mutate({
        dataSource: selectedDataSource,
        fields: selectedFields,
        filters,
        sortBy: sortBy || undefined,
        sortOrder,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const handleRunPreview = useCallback(() => {
    if (!hasReportConfig) return;
    setCurrentPage(1);
    previewMutation.mutate({
      dataSource: selectedDataSource,
      fields: selectedFields,
      filters,
      sortBy: sortBy || undefined,
      sortOrder,
      limit: pageSize,
      offset: 0,
    });
  }, [hasReportConfig, selectedDataSource, selectedFields, filters, sortBy, sortOrder, pageSize, setCurrentPage, previewMutation]);

  const handleExport = useCallback((format: ExportFormat) => {
    if (!hasReportConfig) return;
    exportMutation.mutate({
      dataSource: selectedDataSource,
      fields: selectedFields,
      filters,
      sortBy: sortBy || undefined,
      sortOrder,
      format,
      reportName: reportName || undefined,
    });
  }, [hasReportConfig, selectedDataSource, selectedFields, filters, sortBy, sortOrder, reportName, exportMutation]);

  const handleGenerateChart = useCallback(() => {
    if (!selectedDataSource || !chartGroupByField) return;
    aggregateMutation.mutate({
      dataSource: selectedDataSource,
      groupByField: chartGroupByField,
      aggregateField: chartAggregateField || undefined,
      aggregateFunction: chartAggregateFunction,
      filters,
      limit: 15,
    });
    setActiveView('charts');
  }, [selectedDataSource, chartGroupByField, chartAggregateField, chartAggregateFunction, filters, aggregateMutation, setActiveView]);

  const handleAIGenerate = useCallback(async (prompt: string) => {
    let result: Awaited<ReturnType<typeof aiGenerateMutation.mutateAsync>>;
    try {
      result = await aiGenerateMutation.mutateAsync(prompt);
    } catch {
      // AI config generation failed (e.g. provider error / key blocked). The
      // mutation's onError already surfaced a toast; swallow the rejection so it
      // doesn't bubble up as an uncaught runtime error (the caller in
      // AIReportPanel invokes this fire-and-forget).
      return;
    }
    const filters = result.filters ?? [];
    // Apply AI config to builder state
    setSelectedDataSource(result.dataSource);
    setSelectedFields(result.fields);
    setSortBy(result.sortBy ?? '');
    setSortOrder(result.sortOrder ?? 'asc');
    if (result.chartConfig) {
      setChartType(result.chartConfig.chartType ?? 'bar');
      setChartGroupByField(result.chartConfig.groupByField ?? '');
      setChartAggregateField(result.chartConfig.aggregateField ?? '');
      setChartAggregateFunction(result.chartConfig.aggregateFunction ?? 'count');
    }

    // One AI command populates ALL sections, not just the table. Fire the table
    // preview immediately; if the AI supplied a chart grouping, build the chart
    // in parallel and feed its summary into the AI insights for richer output.
    previewMutation.mutate({
      dataSource: result.dataSource,
      fields: result.fields,
      filters,
      sortBy: result.sortBy || undefined,
      sortOrder: result.sortOrder ?? 'asc',
      limit: pageSize,
      offset: 0,
    });

    const groupByField = result.chartConfig?.groupByField;
    let agg: Awaited<ReturnType<typeof aggregateMutation.mutateAsync>> | undefined;
    if (groupByField) {
      try {
        agg = await aggregateMutation.mutateAsync({
          dataSource: result.dataSource,
          groupByField,
          aggregateField: result.chartConfig?.aggregateField || undefined,
          aggregateFunction: result.chartConfig?.aggregateFunction ?? 'count',
          filters,
          limit: 15,
        });
      } catch {
        // Chart failed (e.g. no aggregable data) — still generate insights below.
      }
    }

    // AI insights over the same config, enriched with the chart summary if we got one.
    aiInsightsMutation.mutate({
      dataSource: result.dataSource,
      fields: result.fields,
      filters,
      sortBy: result.sortBy || undefined,
      sortOrder: result.sortOrder || undefined,
      chartSummary: agg?.summary ?? null,
    });
  }, [aiGenerateMutation, previewMutation, aggregateMutation, aiInsightsMutation, pageSize, setSelectedDataSource, setSelectedFields, setSortBy, setSortOrder, setChartType, setChartGroupByField, setChartAggregateField, setChartAggregateFunction]);

  const handleAIInsights = useCallback(() => {
    if (!hasReportConfig) return;
    aiInsightsMutation.mutate({
      dataSource: selectedDataSource,
      fields: selectedFields,
      filters,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      chartSummary: aggregateMutation.data?.summary ?? null,
    });
  }, [hasReportConfig, selectedDataSource, selectedFields, filters, sortBy, sortOrder, aggregateMutation.data, aiInsightsMutation]);

  // Full reset: clear the config store AND every generated result. The store
  // holds only inputs/config; the produced table, chart, AI insights and AI
  // explanation each live in their own React Query mutation cache, which
  // survive a store reset — so we must reset those too, otherwise switching
  // views after Reset still shows stale data from the section not in focus.
  const handleReset = useCallback(() => {
    resetBuilder();
    previewMutation.reset();
    aggregateMutation.reset();
    aiInsightsMutation.reset();
    aiGenerateMutation.reset();
    exportMutation.reset();
  }, [resetBuilder, previewMutation, aggregateMutation, aiInsightsMutation, aiGenerateMutation, exportMutation]);

  // Any failure across preview / export / aggregate / AI config / AI insights
  // surfaces the same user-facing message instead of a raw backend error.
  const hasError =
    previewMutation.isError ||
    exportMutation.isError ||
    aggregateMutation.isError ||
    aiGenerateMutation.isError ||
    aiInsightsMutation.isError;
  const error = hasError ? 'Insufficient data to generate report' : null;

  // Any data-producing operation in flight — drives the "Building report…"
  // feedback in the report detail panel and the per-tab spinners, so the user
  // sees which sections are still generating (esp. after an AI command that
  // builds table + chart + insights together).
  const isBuilding =
    previewMutation.isPending ||
    aggregateMutation.isPending ||
    aiInsightsMutation.isPending ||
    aiGenerateMutation.isPending;

  return (
    <div className="crb-container">
      {/* Header */}
      <div className="crb-header">
        <div className="crb-title-section">
          <h1>Custom Report Builder</h1>
          <p>Build custom reports by selecting data sources, fields, and filters</p>
        </div>
        <div className="crb-header-actions">
          <button className="crb-btn" onClick={handleReset}>
            <RotateCcw size={16} /> Reset
          </button>
        </div>
      </div>

      {/* AI Panel */}
      <AIReportPanel
        isLoading={aiGenerateMutation.isPending}
        explanation={aiGenerateMutation.data?.explanation ?? ''}
        hasDataSources={!!dataSources}
        onGenerate={handleAIGenerate}
      />

      {error && (
        <div className="crb-error">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <div className="crb-builder-layout">
        {/* Left: Config Panel */}
        <div className="crb-config-panel">
          <DataSourceSection dataSources={dataSources} isLoading={dataSourcesLoading} />
          <FieldsSection currentSource={currentSource} />
          <FiltersSection currentSource={currentSource} />
          <SortSection currentSource={currentSource} />
          <VisualizationSection
            currentSource={currentSource}
            isLoading={aggregateMutation.isPending}
            onGenerateChart={handleGenerateChart}
          />

          {/* Run Preview */}
          <div className="crb-run-preview">
            <button
              className="crb-run-btn"
              onClick={handleRunPreview}
              disabled={!hasReportConfig || previewMutation.isPending}
            >
              <Play size={18} />
              {previewMutation.isPending ? 'Running...' : 'Run Table Preview'}
            </button>
          </div>
        </div>

        {/* Right: Preview Panel */}
        <div className="crb-preview-panel">
          {/* Build progress feedback for the whole detail section */}
          {isBuilding && (
            <div className="crb-building-banner" role="status" aria-live="polite">
              <Loader2 size={15} className="crb-spin-icon" />
              <span>Building report…</span>
              <span className="crb-building-sections">
                {previewMutation.isPending && <span>Table</span>}
                {aggregateMutation.isPending && <span>Chart</span>}
                {aiInsightsMutation.isPending && <span>AI Insights</span>}
              </span>
            </div>
          )}

          {/* View Tabs */}
          <div className="crb-view-tabs">
            <button
              className={`crb-view-tab ${activeView === 'table' ? 'active' : ''}`}
              onClick={() => setActiveView('table')}
            >
              <Table2 size={16} /> Table View
              {previewMutation.isPending ? (
                <Loader2 size={13} className="crb-spin-icon crb-tab-spin" />
              ) : (previewMutation.data?.totalRecords ?? 0) > 0 && (
                <span className="crb-tab-badge">{previewMutation.data!.totalRecords.toLocaleString()}</span>
              )}
            </button>
            <button
              className={`crb-view-tab ${activeView === 'charts' ? 'active' : ''}`}
              onClick={() => setActiveView('charts')}
            >
              <BarChart3 size={16} /> Charts
              {aggregateMutation.isPending ? (
                <Loader2 size={13} className="crb-spin-icon crb-tab-spin" />
              ) : (aggregateMutation.data?.chartData.length ?? 0) > 0 && (
                <span className="crb-tab-badge">{aggregateMutation.data!.chartData.length}</span>
              )}
            </button>
            <button
              className={`crb-view-tab crb-view-tab-ai ${activeView === 'insights' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('insights');
                if (!aiInsightsMutation.data && hasReportConfig && !aiInsightsMutation.isPending) {
                  handleAIInsights();
                }
              }}
            >
              <Sparkles size={16} /> AI Insights
              {aiInsightsMutation.isPending && <Loader2 size={13} className="crb-spin-icon crb-tab-spin" />}
            </button>
          </div>

          {/* View Content */}
          {activeView === 'table' && (
            <TableView
              previewData={previewMutation.data?.data ?? []}
              previewFields={previewMutation.data?.fields ?? []}
              totalRecords={previewMutation.data?.totalRecords ?? 0}
              isLoading={previewMutation.isPending}
              isExporting={exportMutation.isPending}
              onExport={handleExport}
            />
          )}

          {activeView === 'charts' && (
            <ChartView
              chartType={chartType}
              chartData={aggregateMutation.data?.chartData ?? []}
              chartSummary={aggregateMutation.data?.summary ?? null}
              chartGroupByLabel={aggregateMutation.data?.groupByLabel ?? ''}
              chartAggregateLabel={aggregateMutation.data?.aggregateLabel ?? ''}
              isLoading={aggregateMutation.isPending}
            />
          )}

          {activeView === 'insights' && (
            <InsightsView
              insights={aiInsightsMutation.data ?? null}
              isLoading={aiInsightsMutation.isPending}
              hasReportConfig={hasReportConfig}
              onGenerate={handleAIInsights}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomReportBuilder;
